import type Application from "@app/types/Application";
import type BaseCatalog from "@core/FileStructue/Catalog/BaseCatalog";
import type GitSourceData from "@ext/git/core/model/GitSourceData.schema";
import type GitSourceType from "@ext/git/core/model/GitSourceType";
import type Logger from "@ext/loggers/Logger";
import { span, trace } from "@ext/loggers/opentelemetry";
import SourceDataCtx from "@ext/storage/logic/SourceDataProvider/logic/SourceDataCtx";
import SourceType from "@ext/storage/logic/SourceDataProvider/model/SourceType";
import type { Workspace } from "@ext/workspace/Workspace";
import type WorkspaceManager from "@ext/workspace/WorkspaceManager";

const DEFAULT_AUTO_PULL_INTERVAL = 180;
const DEFAULT_AUTO_PULL_DELAY = 100;

const toGitSourceType = (sourceType: SourceType): GitSourceType | null => {
	switch (sourceType) {
		case SourceType.git:
		case SourceType.gitHub:
		case SourceType.gitLab:
		case SourceType.gitVerse:
		case SourceType.gitea:
			return sourceType;
		default:
			return null;
	}
};

export const getAutoPullSourceData = (sourceName: string, sourceType: SourceType): GitSourceData | null => {
	const token = process.env.AUTO_PULL_TOKEN;
	const gitSourceType = toGitSourceType(sourceType);
	if (!token || !gitSourceType) return null;

	const sourceData: GitSourceData = {
		sourceType: gitSourceType,
		domain: sourceName,
		userName: "autopull",
		gitServerUsername: process.env.AUTO_PULL_USERNAME || "autopull",
		userEmail: "autopull",
		token,
	};

	return SourceDataCtx.init<GitSourceData>(sourceData, null, null);
};

export class AutoPull {
	private _wm: WorkspaceManager;
	private _logger: Logger;
	private _pullInterval: number;
	private _pullDelay: number;

	@trace()
	async start(app: Promise<Application>): Promise<void> {
		const { logger, wm } = await app;
		this._logger = logger;

		if (!process.env.AUTO_PULL_TOKEN) {
			span()?.addEvent("auto-pull", { status: "disabled", reason: "no token set" });
			logger.logWarning("AUTO_PULL_TOKEN is not set. Auto-pull is disabled");
			return;
		}

		this._wm = wm;
		this._pullInterval = (Number(process.env.AUTO_PULL_INTERVAL) || DEFAULT_AUTO_PULL_INTERVAL) * 1000;
		this._pullDelay = Number(process.env.AUTO_PULL_DELAY) || DEFAULT_AUTO_PULL_DELAY;

		span()?.addEvent("auto-pull", {
			status: "enabled",
			interval: this._pullInterval,
			delay: this._pullDelay,
		});
		logger.logInfo(`Auto pull enabled with interval: ${this._pullInterval / 1000}s`);

		setTimeout(() => void this._pullCatalogs(this._wm.current()), this._pullInterval);
	}

	@trace()
	private async _pullCatalog(catalog: BaseCatalog): Promise<void> {
		try {
			if (!catalog?.repo?.storage) return;

			const storage = catalog.repo.storage;
			const sourceData = getAutoPullSourceData(await storage.getSourceName(), await storage.getType());
			if (!sourceData) return;

			if (await catalog.repo.isShouldSync({ data: sourceData, shouldFetch: true })) {
				this._logger.logInfo(`Auto-pull started for "${catalog.name}" catalog`);
				await catalog.repo.sync({
					data: sourceData,
					onPull: () => {
						span()?.addEvent("pull", { catalog: catalog.name, status: "success" });
					},
				});
			} else {
				span()?.addEvent("pull", { catalog: catalog.name, status: "skip" });
			}
		} catch (error) {
			span()?.addEvent("pull", { catalog: catalog.name, status: "error", message: String(error) });
			this._logger.logWarning(`Error occurred while auto-pulling in "${catalog.name}" catalog: ${error}`);
		}
	}

	private async _pullCatalogs(workspace: Workspace): Promise<void> {
		const catalogEntries = Array.from(workspace.getAllCatalogs().values());
		for (const catalogEntry of catalogEntries) {
			await this._pullCatalog(catalogEntry);
			await new Promise((r) => setTimeout(r, this._pullDelay));
		}
		setTimeout(() => void this._pullCatalogs(this._wm.current()), this._pullInterval);
	}
}

const initAutoPull = async (app: Promise<Application>): Promise<void> => {
	await new AutoPull().start(app);
};

export default initAutoPull;
